export default class StayCash {
  static empty () {
    return new StayCash({})
  }

  static fromJSON (json, accumulated) {
    const price = json.averagePricing && json.averagePricing.price
    const total = json.totalPriceWithTax
    const nights = json.nights.length
    return new StayCash({
      baseAmountPerNight: StayCash.priceAndPriceAmount(price),
      amountPerNight: StayCash.priceAndPriceAmount(price),
      amountAccumulatedPerNights: StayCash.priceAccAndPriceAmount(total),
      formattedAmountPerNight: StayCash.priceAndFormatted(price),
      formattedAccumulatedAmountPerNight: StayCash.priceAccAndFormatted(total),
      baseUndiscountedAmountPerNight: StayCash.priceAndUndiscountedAmount(price),
      undiscountedAmountPerNight: StayCash.priceAndUndiscountedAmount(price),
      undiscountedAccumulatedAmountPerNights: StayCash.priceAccAndUndiscountedAmount(price, nights),
      formattedUndiscountedAmountPerNight: StayCash.priceAndFormattedUndiscount(price),
      formattedUndiscountedAccumulatedAmountPerNights: StayCash.priceAccAndFormattedUndiscount(price, nights),
      formattedTotalTaxAndFee: StayCash.priceFormattedTaxAndFee(total),
      totalTaxAndFee: StayCash.priceTaxAndFee(total),
      formattedAverageTaxAndFee: StayCash.formattedPriceAverageTaxAndFee(total, nights),
      averageTaxAndFee: StayCash.averagePriceTaxAndFee(total, nights),
      currency: StayCash.priceAndCurrency(price),
      travelAgencyCommissionPercentage: json.commissionPercentage,
      nights: nights,
      accumulated: accumulated,
      voucherApplied: json.voucherApplied
    })
  }

  static priceAndPriceAmount = (price) => price && price.amount
  static priceAccAndPriceAmount = (total) => total && total.amount
  static priceAndFormatted = (price) => price && price.formattedAmount
  static priceAccAndFormatted = (total) => total && total.formattedAmount
  static priceAndUndiscountedAmount = (price) => price && price.undiscountedAmount
  static priceAccAndUndiscountedAmount = (price, nights) => price && price.undiscountedAmount * nights
  static priceAndFormattedUndiscount = (price) => `${price && price.undiscountedAmount && price.currency} ${price && price.undiscountedAmount && price.undiscountedAmount.toFixed(2)}`
  static priceAccAndFormattedUndiscount = (price, nights) => `${price && price.undiscountedAmount * nights && price.currency} ${price && price.undiscountedAmount * nights && (price.undiscountedAmount * nights).toFixed(2)}`
  static priceAndCurrency = (price) => price && price.currency
  static priceFormattedTaxAndFee = (total) => total && total.formattedTotalTaxAndFee
  static priceTaxAndFee = (total) => total && total.totalTaxAndFee
  static averagePriceTaxAndFee = (total, nights) => total && total.totalTaxAndFee / nights
  static formattedPriceAverageTaxAndFee = (total, nights) => total && total.totalTaxAndFee && `${total.currency} ${total.totalTaxAndFee / nights && (total.totalTaxAndFee / nights).toFixed(2)}`

  constructor ({
    baseAmountPerNight, amountPerNight, amountAccumulatedPerNights,
    formattedAccumulatedAmountPerNight, formattedAmountPerNight,
    baseUndiscountedAmountPerNight, undiscountedAmountPerNight,
    undiscountedAccumulatedAmountPerNights, formattedUndiscountedAmountPerNight,
    formattedUndiscountedAccumulatedAmountPerNights, currency,
    travelAgencyCommissionPercentage, nights, accumulated,
    formattedTotalTaxAndFee, totalTaxAndFee, averageTaxAndFee, formattedAverageTaxAndFee, voucherApplied
  }) {
    this.baseAmountPerNight = baseAmountPerNight || 0
    this.amountPerNight = amountPerNight || 0
    this.amountAccumulatedPerNights = amountAccumulatedPerNights || 0
    this.formattedAmountPerNight = formattedAmountPerNight || ''
    this.formattedAccumulatedAmountPerNight = formattedAccumulatedAmountPerNight || ''
    this.baseUndiscountedAmountPerNight = baseUndiscountedAmountPerNight || 0
    this.undiscountedAmountPerNight = undiscountedAmountPerNight || 0
    this.undiscountedAccumulatedAmountPerNights = undiscountedAccumulatedAmountPerNights || 0
    this.formattedUndiscountedAmountPerNight = formattedUndiscountedAmountPerNight || ''
    this.formattedUndiscountedAccumulatedAmountPerNights = formattedUndiscountedAccumulatedAmountPerNights || 0
    this.formattedTotalTaxAndFee = formattedTotalTaxAndFee || ''
    this.totalTaxAndFee = totalTaxAndFee || 0
    this.averageTaxAndFee = averageTaxAndFee || 0
    this.formattedAverageTaxAndFee = formattedAverageTaxAndFee || ''
    this.currency = currency || 'USD'
    this.travelAgencyCommissionPercentage = travelAgencyCommissionPercentage || null
    this.percentageOfDiscountPerNight = this.hasCash() && this.hasDiscount()
      ? Math.round((1 - (this.baseAmountPerNight / this.baseUndiscountedAmountPerNight)) * 100)
      : 0
    this.nights = nights
    this.accumulated = accumulated
    this.voucherApplied = voucherApplied
  }

  hasCash = () => this.amountPerNight > 0 || !!this.voucherApplied
  hasTaxes = () => this.totalTaxAndFee > 0
  hashCashAccumulated = () => this.amountAccumulatedPerNights > 0
  hasDiscount = () => this.undiscountedAmountPerNight > 0
  comesFromTravelAgency = () => this.travelAgencyCommissionPercentage !== null
  hasTravelAgencyCommissionPercentage = () => this.comesFromTravelAgency() && this.travelAgencyCommissionPercentage > 0
  isAccumulated = () => !!(this.hasCash() && this.accumulated?.hotel && this.nights >= 2)
  formattedStayCashAmount = () => this.accumulated?.hotel ? this.formattedUndiscountedAccumulatedAmountPerNights : this.formattedUndiscountedAmountPerNight

  convert (currency) {
    const newAmountPerNight = parseFloat(this.baseAmountPerNight * currency.exchangeRate)
    const newUndiscountedAmountPerNight = parseFloat(this.baseUndiscountedAmountPerNight * currency.exchangeRate)
    const newUndiscountedAccumulatedAmountPerNights = newUndiscountedAmountPerNight * this.nights
    const newAmountAccumulatedPerNights = newAmountPerNight * this.nights
    const newTaxAndFee = parseFloat(this.totalTaxAndFee * currency.exchangeRate)
    const newAverageTaxAndFee = parseFloat(newTaxAndFee / this.nights)
    return new StayCash({
      ...this,
      baseAmountPerNight: this.baseAmountPerNight,
      amountPerNight: newAmountPerNight,
      amountAccumulatedPerNights: newAmountAccumulatedPerNights,
      formattedAccumulatedAmountPerNight: `${currency.code} ${newAmountAccumulatedPerNights.toFixed(2)}`,
      formattedAmountPerNight: `${currency.code} ${newAmountPerNight.toFixed(2)}`,
      baseUndiscountedAmountPerNight: this.baseUndiscountedAmountPerNight,
      undiscountedAmountPerNight: newUndiscountedAmountPerNight,
      undiscountedAccumulatedAmountPerNights: newUndiscountedAccumulatedAmountPerNights,
      formattedUndiscountedAmountPerNight: `${currency.code} ${newUndiscountedAmountPerNight.toFixed(2)}`,
      formattedUndiscountedAccumulatedAmountPerNights: `${currency.code} ${newUndiscountedAccumulatedAmountPerNights.toFixed(2)}`,
      formattedTotalTaxAndFee: `${currency.code} ${newTaxAndFee.toFixed(2)}`,
      averageTaxAndFee: newAverageTaxAndFee,
      formattedAverageTaxAndFee: `${currency.code} ${newAverageTaxAndFee.toFixed(2)}`,
      currency: currency.code
    })
  }
}
